import React, { useEffect, useRef, useState } from "react";
import { BiSolidChevronDown } from "react-icons/bi";
import { Popover, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchExpenseTypes } from "../../store/Slices/ExpenseTypeSlice";
import { getExpenseTypeName } from "../../utils/ExpenseConverter";

const ExpenseTypeSelector = ({
  selectedType,
  setSelectedType,
  placeholder = "Select Type",
  label,
  allowClear = false,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchText, setSearchText] = useState("");
  const searchInputRef = useRef(null);
  const dispatch = useDispatch();
  const ExpenseTypes = useSelector((state) => state.ExpenseTypes);

  useEffect(() => {
    dispatch(fetchExpenseTypes());
  }, [dispatch]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "expense-type-popover" : undefined;

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => searchInputRef.current?.focus(), 80);
    return () => clearTimeout(timer);
  }, [open]);

  const types = Array.isArray(ExpenseTypes.data) ? ExpenseTypes.data : [];

  const filtered = types.filter((row) => {
    if (!searchText.trim()) return true;
    const q = searchText.trim().toLowerCase();
    return String(row.name || "").toLowerCase().includes(q);
  });

  const displayName = selectedType ? getExpenseTypeName(selectedType) : "";

  return (
    <div className="flex flex-col px-0 gap-y-1 relative w-full">
      {label && <p className="text-sm font-bold text-main px-1">{label}</p>}
      <div
        className="relative text-left cursor-pointer transition-colors ease-in-out duration-200 border border-line hover:border-main bg-card text-ink px-4 py-3 rounded-lg font-quicksand font-bold text-sm flex gap-x-6 items-center active:scale-[.99]"
        onClick={handleClick}
      >
        <div className="flex items-center justify-between w-full min-w-0">
          <div className="w-full outline-none text-left bg-transparent truncate">
            {displayName || placeholder}
          </div>
          <BiSolidChevronDown className="text-[1.5rem] cursor-pointer text-muted shrink-0" />
        </div>
      </div>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            backgroundColor: "#ffffff",
            border: "1px solid #E5E9EB",
            width: "100%",
            maxWidth: "300px",
            overflow: "hidden",
            marginTop: "10px",
            boxShadow: "0 10px 30px rgba(16,69,70,0.12), 0 2px 8px rgba(16,69,70,0.08)",
          },
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Typography
          sx={{
            p: 2,
            backgroundColor: "#ffffff",
            overflow: "hidden",
            borderRadius: "12px",
          }}
        >
          <div className="bg-card text-ink font-quicksand flex flex-col justify-center items-center rounded-lg">
            <input
              ref={searchInputRef}
              type="text"
            placeholder="Search type…"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="w-full mb-3 px-3 py-2 rounded-lg text-ink text-sm outline-none"
            />
            <div className="w-full flex flex-col justify-between gap-y-3 items-start max-h-[240px] overflow-y-auto">
              {allowClear && (
                <div
                  className="flex gap-x-3 items-center cursor-pointer hover:bg-subtle rounded-lg px-2 py-1 transition-colors"
                  onClick={() => {
                    setSelectedType("");
                    handleClose();
                  }}
                >
                  <input
                    type="checkbox"
                    className="mr-1 h-4 w-4 rounded border border-line accent-main"
                    checked={!selectedType}
                    readOnly
                  />
                  <span>All types</span>
                </div>
              )}
              {filtered.map((row) => (
                <div
                  key={row._id}
                  className="flex gap-x-3 items-center cursor-pointer hover:bg-subtle rounded-lg px-2 py-1 transition-colors"
                  onClick={() => {
                    setSelectedType(row);
                    handleClose();
                  }}
                >
                  <input
                    type="checkbox"
                    className="mr-1 h-4 w-4 rounded border border-line accent-main"
                    checked={selectedType?._id === row._id}
                    readOnly
                  />
                  <span>{row.name}</span>
                </div>
              ))}
              {!filtered.length && (
                <span className="text-sm text-muted">No types found</span>
              )}
            </div>
          </div>
        </Typography>
      </Popover>
    </div>
  );
};

export default ExpenseTypeSelector;
